/**
 * Live Activity + widget-data bridge (Sleep Suite).
 *
 * Talks to the local `LiveActivityPlugin.swift` (registered under jsName
 * "LiveActivity") for the lock-screen Live Activity and the home-screen
 * widget's App Group JSON.
 *
 * The Live Activity has two content modes, both driven from here:
 *  - countdown (`syncFromSchedule`) — counts down to the next predicted nap
 *    or bedtime. Sends no `mode`, exactly as the v1.2.0 build did.
 *  - sleeping (`syncSleeping`) — counts up from the moment an in-progress
 *    sleep began, with an optional expected wake time.
 * Both share the single activity: switching between them is an update, which
 * is why `mode` is part of the dedupe key.
 *
 * Thenable-proxy rule: `registerPlugin()` returns a Proxy that intercepts
 * every property get, including `then` — awaiting it (or returning it from an
 * async function) hangs the caller forever. The handle is therefore kept in a
 * plain wrapper object (`{ api }`) and only its METHODS are ever awaited.
 *
 * Silent no-op everywhere it can't work: web, Android, iOS < 16.1
 * (plugin reports unsupported), plugin missing, or free plan (the driver
 * only calls in with Premium schedule data).
 */

import { registerPlugin } from '@capacitor/core';
import { isIOS, isNative } from '../shared/utils/platform';

let wrapper = null; // { api } — plain object around the plugin proxy

function getApi() {
  if (!isNative() || !isIOS()) return null;
  if (!wrapper) {
    try {
      wrapper = { api: registerPlugin('LiveActivity') };
    } catch {
      return null;
    }
  }
  return wrapper.api;
}

let supportedCache = null;
let activityStarted = false;
let lastContentKey = null;

/** True when Live Activities are available (iOS 16.1+, native plugin present). */
export async function isSupported() {
  if (supportedCache !== null) return supportedCache;
  const api = getApi();
  if (!api) return false;
  try {
    const res = await api.isSupported();
    supportedCache = !!res?.isSupported;
  } catch {
    supportedCache = false;
  }
  return supportedCache;
}

/**
 * Start or update the single activity with `payload`, deduping on content so
 * a 60 s driver tick doesn't hammer ActivityKit. `mode` is part of the key:
 * a countdown and a sleeping payload never look identical, so switching modes
 * always pushes an update.
 */
async function applyPayload(api, payload) {
  const contentKey = [
    payload.mode || 'countdown',
    payload.babyName,
    payload.eventLabel,
    payload.startTimeISO || '',
    payload.targetTimeISO,
  ].join('|');
  if (activityStarted && contentKey === lastContentKey) return; // nothing changed

  try {
    let live = activityStarted;
    if (live) {
      // `updated: false` means there is no LIVE activity to update — the user
      // swiped it away (very likely now that a sleeping activity sits there for
      // hours). Treat it as "restart", not as success: otherwise the module
      // stayed convinced an activity existed and no Live Activity ever appeared
      // again until the app was force-quit.
      const res = await api.updateActivity(payload);
      if (res && res.updated === false) live = false;
    }
    if (!live) {
      await api.startActivity(payload);
      activityStarted = true;
    }
    lastContentKey = contentKey;
  } catch {
    // An update against a dead activity: try one fresh start, then give up.
    try {
      await api.startActivity(payload);
      activityStarted = true;
      lastContentKey = contentKey;
    } catch {
      activityStarted = false;
      lastContentKey = null;
    }
  }
}

/**
 * Drive the Live Activity from schedule data: show a countdown to the next
 * upcoming event (predicted nap or bedtime); end the activity once bedtime
 * has passed. `next` is { label, time } or null (nothing left today).
 */
export async function syncFromSchedule(next, babyName) {
  if (!(await isSupported())) return;
  const api = getApi();
  if (!api) return;

  if (!next || new Date(next.time) <= new Date()) {
    await endActivity();
    return;
  }

  await applyPayload(api, {
    babyName: babyName || 'Baby',
    eventLabel: next.label,
    targetTimeISO: next.time,
  });
}

/**
 * Drive the Live Activity from an in-progress sleep: the lock screen counts
 * UP from `startISO` ("Asleep · 42:10"), with "usually wakes around HH:mm"
 * when `expectedWakeISO` is known — start + the usual nap length for a nap,
 * the baby's usual morning wake for a night sleep (see
 * features/sleep/expectedWake.js).
 *
 * Takes priority over `syncFromSchedule` in the driver — while the baby is
 * asleep the elapsed timer is what the parent wants on the lock screen.
 * A missing `startISO` is a no-op: ending the activity (or reverting to the
 * countdown) is the driver's decision, not this function's.
 */
export async function syncSleeping({ babyName, startISO, expectedWakeISO } = {}) {
  if (!startISO) return;
  if (!(await isSupported())) return;
  const api = getApi();
  if (!api) return;

  await applyPayload(api, {
    babyName: babyName || 'Baby',
    eventLabel: 'Asleep',
    mode: 'sleeping',
    startTimeISO: startISO,
    // The native side treats target == start as "no expected wake time".
    targetTimeISO: expectedWakeISO || startISO,
  });
}

/** End the Live Activity (bedtime passed, reminders toggled off, sign-out). */
export async function endActivity() {
  activityStarted = false;
  lastContentKey = null;
  const api = getApi();
  if (!api) return;
  try {
    await api.endActivity({});
  } catch {
    /* nothing running — fine */
  }
}

/**
 * Write the home-screen widget's data blob (App Group JSON) — called by the
 * driver after each schedule success and whenever the sleep state changes.
 * Whatever object it is handed is forwarded verbatim; the widget currently
 * reads { babyName, nextLabel, nextTimeISO, lastFeedISO, sleepingSinceISO }
 * and ignores unknown keys.
 */
export async function setWidgetData(data) {
  const api = getApi();
  if (!api) return;
  try {
    // The native plugin requires `json` to be a string (call.getString("json")).
    await api.setWidgetData({ json: typeof data === 'string' ? data : JSON.stringify(data) });
  } catch {
    /* widget not installed / plugin missing — silent */
  }
}
